import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  Search,
  X,
  Loader2,
  Wallet,
  ArrowUpCircle,
  History,
  CheckCircle,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  RefreshCw,
} from "lucide-react";
import useToken from "../../../../hooks/auth/useToken";
import { usersService } from "../../../../services/administration/usersService";
import { Country, User } from "../../../../types/features/administration/User";
import useCountries from "../../../../hooks/administration/countries/useCountries";
import useWallets from "../../../../hooks/administration/wallets/useWallets";


interface FilterState {
  search: string;
}

interface SearchedUser {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phonenumber: string;
  country?: Country;
}

const StatusChangeModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (status: "COMPLETED" | "CANCELLED" | "PENDING") => void;
  isLoading: boolean;
  currentStatus: string;
  transaction: any;
}> = ({ isOpen, onClose, onConfirm, isLoading, currentStatus, transaction }) => {
  const [selectedStatus, setSelectedStatus] = useState<"COMPLETED" | "CANCELLED" | "PENDING">(
    currentStatus as "COMPLETED" | "CANCELLED" | "PENDING"
  );

  useEffect(() => {
    if (isOpen) {
      setSelectedStatus(currentStatus as "COMPLETED" | "CANCELLED" | "PENDING");
    }
  }, [isOpen, currentStatus]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedStatus !== currentStatus) {
      onConfirm(selectedStatus);
    }
  };

  if (!isOpen || !transaction) return null;

  const getStatusColor = (status: string) => {
    switch (status) {
      case "COMPLETED":
        return "text-green-600 bg-green-50 border-green-200";
      case "CANCELLED":
        return "text-red-600 bg-red-50 border-red-200";
      default:
        return "text-yellow-600 bg-yellow-50 border-yellow-200";
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-md shadow-2xl flex flex-col max-h-[90vh]">
        <div className="flex justify-between items-center p-6 pb-4 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <RefreshCw className="w-6 h-6 text-blue-600" />
            Changer le statut de la transaction
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={isLoading}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Type :</span>
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                <ArrowUpCircle className="w-3 h-3" />
                Recharge
              </span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Montant :</span>
              <span className="font-bold text-lg text-green-600">
                +{transaction.amount.toLocaleString()} {transaction.country?.devise}
              </span>
            </div>

            <div className="flex justify-between items-start">
              <span className="text-sm text-gray-600">Utilisateur :</span>
              <div className="text-right">
                <span className="text-sm font-medium block">
                  {transaction.user?.first_name} {transaction.user?.last_name}
                </span>
                <span className="text-xs text-gray-500 block">{transaction.user?.email}</span>
              </div>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Pays :</span>
              <span className="text-sm font-medium">
                {transaction.country?.name} ({transaction.country?.code})
              </span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Date :</span>
              <span className="text-xs text-gray-500">
                {transaction.created_at ? new Date(transaction.created_at).toLocaleString("fr-FR") : "-"}
              </span>
            </div>

            {transaction.description && (
              <div className="flex justify-between items-start">
                <span className="text-sm text-gray-600">Description :</span>
                <span className="text-sm text-gray-700 max-w-[200px] text-right break-words">
                  {transaction.description}
                </span>
              </div>
            )}

            <div className="flex justify-between items-center pt-2 border-t border-gray-200">
              <span className="text-sm font-medium text-gray-700">Statut actuel :</span>
              <span
                className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(currentStatus)}`}
              >
                {currentStatus === "COMPLETED" && <CheckCircle className="w-3 h-3" />}
                {currentStatus === "COMPLETED" && "Complété"}
                {currentStatus === "CANCELLED" && "Annulé"}
                {currentStatus === "PENDING" && "En attente"}
              </span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">Nouveau statut</label>
            <div className="space-y-3">
              <label
                className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${selectedStatus === "COMPLETED" ? "border-green-500 bg-green-50" : "border-gray-200 hover:bg-gray-50"}`}
              >
                <input
                  type="radio"
                  value="COMPLETED"
                  checked={selectedStatus === "COMPLETED"}
                  onChange={(e) => setSelectedStatus(e.target.value as any)}
                  className="w-4 h-4 text-green-600 flex-shrink-0"
                  disabled={isLoading}
                />
                <div className="flex-1">
                  <span className="font-medium text-green-700">Complété</span>
                  <p className="text-xs text-gray-500 mt-0.5">Marquer la transaction comme terminée avec succès</p>
                </div>
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
              </label>

              <label
                className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${selectedStatus === "PENDING" ? "border-yellow-500 bg-yellow-50" : "border-gray-200 hover:bg-gray-50"}`}
              >
                <input
                  type="radio"
                  value="PENDING"
                  checked={selectedStatus === "PENDING"}
                  onChange={(e) => setSelectedStatus(e.target.value as any)}
                  className="w-4 h-4 text-yellow-600 flex-shrink-0"
                  disabled={isLoading}
                />
                <div className="flex-1">
                  <span className="font-medium text-yellow-700">En attente</span>
                  <p className="text-xs text-gray-500 mt-0.5">Mettre la transaction en attente de traitement</p>
                </div>
                <Loader2 className="w-5 h-5 text-yellow-600 flex-shrink-0" />
              </label>

              <label
                className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${selectedStatus === "CANCELLED" ? "border-red-500 bg-red-50" : "border-gray-200 hover:bg-gray-50"}`}
              >
                <input
                  type="radio"
                  value="CANCELLED"
                  checked={selectedStatus === "CANCELLED"}
                  onChange={(e) => setSelectedStatus(e.target.value as any)}
                  className="w-4 h-4 text-red-600 flex-shrink-0"
                  disabled={isLoading}
                />
                <div className="flex-1">
                  <span className="font-medium text-red-700">Annulé</span>
                  <p className="text-xs text-gray-500 mt-0.5">Annuler la transaction</p>
                </div>
                <X className="w-5 h-5 text-red-600 flex-shrink-0" />
              </label>
            </div>
          </div>

          {selectedStatus === "CANCELLED" && currentStatus !== "CANCELLED" && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-700 flex items-start gap-2">
                <X className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>
                  Attention : L'annulation d'une transaction n'affecte pas automatiquement le solde du wallet.
                </span>
              </p>
            </div>
          )}
        </div>

        <div className="flex space-x-3 p-6 pt-4 border-t border-gray-200 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium disabled:opacity-50"
            disabled={isLoading}
          >
            Annuler
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={selectedStatus === currentStatus || isLoading}
            className={`flex-1 text-white px-4 py-2.5 rounded-lg transition-colors flex items-center justify-center space-x-2 disabled:opacity-50 font-medium ${
              selectedStatus === "CANCELLED"
                ? "bg-red-600 hover:bg-red-700"
                : selectedStatus === "COMPLETED"
                  ? "bg-green-600 hover:bg-green-700"
                  : "bg-blue-600 hover:bg-blue-700"
            }`}
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Changement...</span>
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4" />
                <span>Changer le statut</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const RechargeModal: React.FC<{
  countries: Country[];
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (data: { user_id: string; country_id: number; amount: number; description?: string }) => void;
  isLoading: boolean;
}> = ({ countries, isOpen, onClose, onConfirm, isLoading }) => {
  const { token } = useToken();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<SearchedUser[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedUser, setSelectedUser] = useState<SearchedUser | null>(null);
  const [form, setForm] = useState({ country_id: 0, amount: "", description: "" });
  const [errors, setErrors] = useState<{ user?: string; country_id?: string; amount?: string }>({});
  const searchRef = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const selectedCountry = countries.find((c) => c.id === form.country_id);
  const devise = selectedCountry?.devise || "";

  useEffect(() => {
    if (isOpen) {
      setSearchQuery("");
      setSearchResults([]);
      setSelectedUser(null);
      setForm({ country_id: 0, amount: "", description: "" });
      setErrors({});
      setShowDropdown(false);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchQuery(value);
      setSelectedUser(null);
      if (errors.user) setErrors((prev) => ({ ...prev, user: undefined }));

      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

      if (value.length < 2) {
        setSearchResults([]);
        setShowDropdown(false);
        return;
      }

      searchTimeoutRef.current = setTimeout(async () => {
        setIsSearching(true);
        try {
          const results = await usersService.searchUsers(token, value);
          setSearchResults(results as any);
          setShowDropdown(results.length > 0);
        } catch {
          setSearchResults([]);
        } finally {
          setIsSearching(false);
        }
      }, 400);
    },
    [token, errors.user]
  );

  const handleSelectUser = (user: SearchedUser) => {
    setSelectedUser(user);
    setSearchQuery(`${user.first_name || ""} ${user.last_name || ""} (${user.email})`);
    setShowDropdown(false);

    if (user.country?.id) {
      setForm((prev) => ({ ...prev, country_id: user.country!.id }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: { user?: string; country_id?: string; amount?: string } = {};
    if (!selectedUser) newErrors.user = "Veuillez sélectionner un utilisateur";
    if (!form.country_id) newErrors.country_id = "Le pays est requis";
    if (!form.amount || Number(form.amount) <= 0) newErrors.amount = "Le montant doit être supérieur à 0";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm() && selectedUser) {
      onConfirm({
        user_id: selectedUser.id,
        country_id: form.country_id,
        amount: Number(form.amount),
        description: form.description || undefined,
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 w-full max-w-lg shadow-2xl">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <ArrowUpCircle className="w-6 h-6 text-green-600" />
            Recharger un Wallet
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div ref={searchRef} className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">Rechercher un utilisateur</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                className={`w-full pl-10 pr-10 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${errors.user ? "border-red-300" : "border-gray-300"}`}
                placeholder="Tapez un nom, email ou telephone..."
                disabled={isLoading}
              />
              {isSearching && (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
              )}
              {selectedUser && !isSearching && (
                <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />
              )}
            </div>
            {errors.user && <p className="mt-1 text-sm text-red-600">{errors.user}</p>}

            {showDropdown && searchResults.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {searchResults.map((user) => (
                  <button
                    key={user.id}
                    type="button"
                    onClick={() => handleSelectUser(user)}
                    className="w-full px-4 py-3 text-left hover:bg-green-50 flex items-center gap-3 border-b border-gray-100 last:border-0 transition-colors"
                  >
                    <div className="w-9 h-9 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                      <Wallet className="w-4 h-4 text-green-600" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {user.first_name || ""} {user.last_name || ""}
                      </p>
                      <p className="text-xs text-gray-500 truncate">{user.email}</p>
                      {user.phonenumber && <p className="text-xs text-gray-400">{user.phonenumber}</p>}
                    </div>
                    {user.country && (
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded flex-shrink-0">
                        {user.country.code}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}

            {showDropdown && searchResults.length === 0 && !isSearching && searchQuery.length >= 2 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-4 text-center text-sm text-gray-500">
                Aucun utilisateur trouve pour "{searchQuery}"
              </div>
            )}
          </div>

          {selectedUser && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-200 flex items-center justify-center">
                <Wallet className="w-5 h-5 text-green-700" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-green-900">
                  {selectedUser.first_name || ""} {selectedUser.last_name || ""}
                </p>
                <p className="text-xs text-green-700 truncate">{selectedUser.email}</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setSelectedUser(null);
                  setSearchQuery("");
                  setForm((prev) => ({ ...prev, country_id: 0 }));
                }}
                className="text-green-600 hover:text-red-500 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Pays (Devise)</label>
            <select
              value={form.country_id}
              onChange={(e) => {
                setForm((prev) => ({ ...prev, country_id: Number(e.target.value) }));
                if (errors.country_id) setErrors((prev) => ({ ...prev, country_id: undefined }));
              }}
              className={`w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-gray-800 ${errors.country_id ? "border-red-300" : "border-gray-300"}`}
              disabled={isLoading}
            >
              <option value={0}>-- Selectionner un pays --</option>
              {countries.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} ({c.code}) - {c.devise}
                </option>
              ))}
            </select>
            {errors.country_id && <p className="mt-1 text-sm text-red-600">{errors.country_id}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Montant</label>
            <div className="relative">
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.amount}
                onChange={(e) => {
                  setForm((prev) => ({ ...prev, amount: e.target.value }));
                  if (errors.amount) setErrors((prev) => ({ ...prev, amount: undefined }));
                }}
                className={`w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${devise ? "pr-16" : ""} ${errors.amount ? "border-red-300" : "border-gray-300"}`}
                placeholder="Ex: 5000"
                disabled={isLoading}
              />
              {devise && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-gray-500">
                  {devise}
                </span>
              )}
            </div>
            {errors.amount && <p className="mt-1 text-sm text-red-600">{errors.amount}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <input
              type="text"
              value={form.description}
              onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="Ex: Recharge bonus"
              disabled={isLoading}
            />
          </div>

          {selectedUser && form.country_id > 0 && Number(form.amount) > 0 && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm space-y-1">
              <p className="font-medium text-gray-700">Resume de la recharge</p>
              <p className="text-gray-600">
                <span className="font-medium">Utilisateur :</span> {selectedUser.first_name} {selectedUser.last_name}
              </p>
              <p className="text-gray-600">
                <span className="font-medium">Montant :</span>{" "}
                <span className="font-bold text-green-600">
                  {Number(form.amount).toLocaleString()} {devise}
                </span>
              </p>
            </div>
          )}

          <div className="flex space-x-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              disabled={isLoading}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="flex-1 text-white px-4 py-2.5 rounded-lg transition-colors flex items-center justify-center space-x-2 disabled:opacity-50 font-medium bg-green-600 hover:bg-green-700"
              disabled={isLoading || !selectedUser}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Recharge en cours...</span>
                </>
              ) : (
                <>
                  <ArrowUpCircle className="w-4 h-4" />
                  <span>Recharger</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Rechargements: React.FC = () => {
  const {
    histories,
    historyLoading,
    rechargeWallet,
    getHistories,
    searchHistories,
    getStats,
    historiesPagination,
    updateTransactionStatus,
  } = useWallets({});
  const { countries } = useCountries({});
  const { token } = useToken();

  const [historyFilters, setHistoryFilters] = useState<FilterState>({ search: "" });
  const [historyPage, setHistoryPage] = useState(1);
  const [historySortBy, setHistorySortBy] = useState<string>("created_at");
  const [historySortOrder, setHistorySortOrder] = useState<string>("desc");
  const [showRechargeModal, setShowRechargeModal] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<any>(null);
  const [isStatusUpdating, setIsStatusUpdating] = useState(false);

  useEffect(() => {
    if (token) {
      getStats();
    }
  }, [token]);

  useEffect(() => {
    if (historyFilters.search) {
      searchHistories(historyFilters.search, historyPage, 50, "RECHARGE", historySortBy, historySortOrder);
    } else {
      getHistories(historyPage, 50, undefined, undefined, "RECHARGE", historySortBy, historySortOrder);
    }
  }, [historyPage, historyFilters.search, historySortBy, historySortOrder]);

  const handleHistorySort = (sortBy: string) => {
    if (historySortBy === sortBy) {
      setHistorySortOrder(historySortOrder === "asc" ? "desc" : "asc");
    } else {
      setHistorySortBy(sortBy);
      setHistorySortOrder("asc");
    }
    setHistoryPage(1);
  };

  const filteredHistories = histories || [];
  const totalHistoryPages = historiesPagination?.total_pages || 1;
  const currentHistories = filteredHistories;

  const handleRecharge = async (data: {
    user_id: string;
    country_id: number;
    amount: number;
    description?: string;
  }) => {
    setIsActionLoading(true);
    try {
      await rechargeWallet(data);
      setShowRechargeModal(false);
      getStats();
      if (historyFilters.search) {
        searchHistories(historyFilters.search, historyPage, 50, "RECHARGE", historySortBy, historySortOrder);
      } else {
        getHistories(historyPage, 50, undefined, undefined, "RECHARGE", historySortBy, historySortOrder);
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      alert(err?.response?.data?.message || "Erreur lors de la recharge");
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleStatusChange = async (status: "COMPLETED" | "CANCELLED" | "PENDING") => {
    if (!selectedTransaction) return;

    setIsStatusUpdating(true);
    try {
      await updateTransactionStatus(selectedTransaction.id, selectedTransaction.wallet_id, status);
      setShowStatusModal(false);
      setSelectedTransaction(null);
      if (historyFilters.search) {
        searchHistories(historyFilters.search, historyPage, 50, "RECHARGE", historySortBy, historySortOrder);
      } else {
        getHistories(historyPage, 50, undefined, undefined, "RECHARGE", historySortBy, historySortOrder);
      }
      getStats();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      alert(err?.response?.data?.message || "Erreur lors du changement de statut");
    } finally {
      setIsStatusUpdating(false);
    }
  };

  return (
    <div className="p-2 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Rechargements Wallet</h2>
          <p className="text-gray-600 text-sm mt-1">Historique des rechargements de portefeuille</p>
        </div>
        <button
          onClick={() => setShowRechargeModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
        >
          <ArrowUpCircle className="w-5 h-5" />
          Nouveau Rechargement
        </button>
      </div>

      <div>
        <div className="p-4 flex justify-between items-center border-b border-gray-100">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher dans l'historique..."
                value={historyFilters.search}
                onChange={(e) => {
                  setHistoryFilters({ search: e.target.value });
                  setHistoryPage(1);
                }}
                className="pl-10 pr-4 py-2 w-full border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Statut</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">
                  <button
                    onClick={() => handleHistorySort("name")}
                    className="flex items-center gap-1 hover:text-gray-900 transition-colors"
                  >
                    Utilisateur
                    {historySortBy === "name" ? (
                      historySortOrder === "asc" ? (
                        <ArrowUp className="w-4 h-4" />
                      ) : (
                        <ArrowDown className="w-4 h-4" />
                      )
                    ) : (
                      <ArrowUpDown className="w-4 h-4 opacity-30" />
                    )}
                  </button>
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Pays / Devise</th>
                <th className="px-6 py-4 text-right text-sm font-medium text-gray-600">
                  <button
                    onClick={() => handleHistorySort("amount")}
                    className="flex items-center gap-1 hover:text-gray-900 transition-colors ml-auto"
                  >
                    Montant
                    {historySortBy === "amount" ? (
                      historySortOrder === "asc" ? (
                        <ArrowUp className="w-4 h-4" />
                      ) : (
                        <ArrowDown className="w-4 h-4" />
                      )
                    ) : (
                      <ArrowUpDown className="w-4 h-4 opacity-30" />
                    )}
                  </button>
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Description</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">
                  <button
                    onClick={() => handleHistorySort("created_at")}
                    className="flex items-center gap-1 hover:text-gray-900 transition-colors"
                  >
                    Date
                    {historySortBy === "created_at" ? (
                      historySortOrder === "asc" ? (
                        <ArrowUp className="w-4 h-4" />
                      ) : (
                        <ArrowDown className="w-4 h-4" />
                      )
                    ) : (
                      <ArrowUpDown className="w-4 h-4 opacity-30" />
                    )}
                  </button>
                </th>
                <th className="px-6 py-4 text-center text-sm font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {historyLoading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-2"></div>
                      <p className="text-gray-600 text-sm">Chargement de l'historique...</p>
                    </div>
                  </td>
                </tr>
              ) : currentHistories.length > 0 ? (
                currentHistories.map((h) => (
                  <tr key={h.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      {h.status === "COMPLETED" ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          <CheckCircle className="w-3 h-3" /> Complete
                        </span>
                      ) : h.status === "CANCELLED" ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                          <X className="w-3 h-3" /> Annule
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                          <Loader2 className="w-3 h-3" /> En attente
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-gray-800">
                          {h.user?.first_name} {h.user?.last_name}
                        </p>
                        <p className="text-xs text-gray-500">{h.user?.email}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-gray-800">{h.country?.name || "-"}</p>
                        <p className="text-xs text-gray-500">
                          {h.country?.code} - {h.country?.devise}
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <span className="font-bold text-green-600">+{h.amount.toLocaleString()}</span>
                      <span className="text-xs text-gray-500 ml-1">{h.country?.devise}</span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">{h.description || "-"}</td>
                    <td className="px-6 py-4 text-xs text-gray-500">
                      {h.created_at ? new Date(h.created_at).toLocaleString("fr-FR") : "-"}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => {
                            setSelectedTransaction(h);
                            setShowStatusModal(true);
                          }}
                          className="flex items-center gap-1 px-2.5 py-1.5 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-xs font-medium"
                          title="Changer le statut"
                        >
                          <RefreshCw className="w-3.5 h-3.5" />
                          Statut
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                    {historyFilters.search
                      ? "Aucun rechargement trouve pour cette recherche"
                      : "Aucun rechargement trouve"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            {historyLoading ? (
              <span className="text-gray-400">Chargement...</span>
            ) : (
              <span>
                Page {historyPage} sur {totalHistoryPages} ({historiesPagination?.total || 0} rechargements au total)
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setHistoryPage(Math.max(1, historyPage - 1))}
              disabled={historyPage === 1 || historyLoading}
              className="px-3 py-1 border border-gray-200 rounded text-sm disabled:opacity-50 hover:bg-gray-100"
            >
              Precedent
            </button>
            <button
              onClick={() => setHistoryPage(Math.min(totalHistoryPages, historyPage + 1))}
              disabled={historyPage === totalHistoryPages || historyLoading}
              className="px-3 py-1 border border-gray-200 rounded text-sm disabled:opacity-50 hover:bg-gray-100"
            >
              Suivant
            </button>
          </div>
        </div>
      </div>

      <RechargeModal
        countries={countries || []}
        isOpen={showRechargeModal}
        onClose={() => setShowRechargeModal(false)}
        onConfirm={handleRecharge}
        isLoading={isActionLoading}
      />

      <StatusChangeModal
        isOpen={showStatusModal}
        onClose={() => {
          setShowStatusModal(false);
          setSelectedTransaction(null);
        }}
        onConfirm={handleStatusChange}
        isLoading={isStatusUpdating}
        currentStatus={selectedTransaction?.status || "PENDING"}
        transaction={selectedTransaction}
      />
    </div>
  );
};

export default Rechargements;
